import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import {
  CancellationReviewPanel,
  GraceBanner,
  PaymentFailureBanner,
  SubscriptionSummary,
  formatDate,
  metricMeta,
  quotaTone,
} from "../components/billing";
import { supportHref } from "../components/support-link";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type CancellationReview,
  type EntitlementSnapshotView,
  type ManualAgreementView,
  type SafeInvoiceView,
  type SafeSubscriptionView,
  type SubscriptionSyncView,
  type UsageSummary,
} from "../lib/api";

/**
 * Billing overview (Phase 9, section 36/43).
 *
 * A calm, authenticated summary of a tenant's billing: current plan, status, period,
 * renewal/end, trial, grace/payment state, entitlement + usage summaries, invoices
 * link, the hosted Customer Portal action, scheduled changes, manual-billing status,
 * and a visible, non-hidden cancellation path. It renders ONLY safe server views —
 * never a provider object, secret, or card detail — and never uses urgency, fake
 * scarcity, or a countdown.
 */

export interface BillingBundle {
  subscription: SafeSubscriptionView;
  entitlements: EntitlementSnapshotView | null;
  usage: UsageSummary | null;
  invoices: SafeInvoiceView[] | null;
  manual: ManualAgreementView[] | null;
  cancellation: CancellationReview | null;
}

const PAYMENT_ISSUE_STATES = new Set(["PAST_DUE", "UNPAID"]);

/**
 * The outcome of a cancel / resume attempt, kept apart per action so a message about
 * one is never shown next to the other. `tone: "error"` is used whenever the server
 * did NOT do the thing — including the honest "we could not do this here, use the
 * portal" case — so the dashboard never implies a change that did not happen.
 */
export interface PlanActionFeedback {
  scope: "cancel" | "resume";
  tone: "notice" | "error";
  message: string;
}

/** In-app plan lifecycle actions. Cancelling and reversing cost one click each. */
export interface PlanActions {
  onCancel: () => void;
  onResume: () => void;
  cancelPending: boolean;
  resumePending: boolean;
  feedback: PlanActionFeedback | null;
}

/**
 * Plain-language outcome of a provider re-read. Never claims a plan changed when it
 * did not, and never blames the customer for a delayed provider notification.
 */
export function syncMessageFor(result: SubscriptionSyncView): string {
  if (result.synced) {
    return result.planCode
      ? `Your billing is up to date. Current plan: ${result.planCode}.`
      : "Your billing is up to date.";
  }
  if (result.reason === "no_subscription") {
    return "No paid subscription was found for this workspace at the payment provider. If you have just paid, give it a moment and check again.";
  }
  if (result.reason === "no_billing_account") {
    return "This workspace has not been to the payment provider yet, so there was nothing to check.";
  }
  return "Nothing new to apply — your plan is unchanged.";
}

export function BillingOverview({
  bundle,
  onOpenPortal,
  portalPending,
  portalError,
  onSync,
  syncPending,
  syncMessage,
  plan,
}: {
  bundle: BillingBundle;
  onOpenPortal: () => void;
  portalPending: boolean;
  portalError: string | null;
  onSync: () => void;
  syncPending: boolean;
  syncMessage: string | null;
  plan: PlanActions;
}): JSX.Element {
  const { subscription, entitlements, usage, invoices, manual, cancellation } = bundle;
  const paymentIssue = PAYMENT_ISSUE_STATES.has(subscription.status);
  const scheduled = subscription.scheduledChange;
  const scheduledCancel =
    scheduled !== null || subscription.cancelAtPeriodEnd || subscription.cancellationRequested;
  // The cancellation review knows the real downgrade target for this plan; the
  // subscription view falls back to FREE. Prefer the more accurate one.
  const scheduledTarget = cancellation?.downgradesTo ?? scheduled?.resultingPlanCode ?? "Free";
  const scheduledEffectiveIso = scheduled?.effectiveAtIso ?? subscription.currentPeriodEndIso;
  const resumeFeedback = plan.feedback?.scope === "resume" ? plan.feedback : null;
  const cancelFeedback = plan.feedback?.scope === "cancel" ? plan.feedback : null;
  const usageWarnings = usage?.items.filter((i) => i.quota.state !== "ok") ?? [];
  const enforcementNotes =
    entitlements?.entitlements.filter((e) => e.enforcement !== "ALLOWED" && e.enforcement !== "SAFETY_OVERRIDE") ?? [];

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      {subscription.inGrace ? (
        <GraceBanner graceEndsIso={subscription.graceEndsIso} onOpenPortal={onOpenPortal} portalPending={portalPending} />
      ) : null}
      {paymentIssue ? (
        <PaymentFailureBanner status={subscription.status} onOpenPortal={onOpenPortal} portalPending={portalPending} />
      ) : null}

      <Card title="Your plan">
        <SubscriptionSummary subscription={subscription} />
        {scheduledCancel ? (
          <div className="banner-item banner-item--warn" role="status" style={{ marginTop: "0.75rem" }}>
            <div className="banner-item__message" style={{ display: "grid", gap: "0.35rem" }}>
              <strong className="banner-item__label">Your plan is scheduled to stop renewing</strong>
              <p style={{ margin: 0 }}>
                {scheduled?.description ??
                  "Your plan is scheduled to stop renewing at the end of the period you have already paid for."}
              </p>
              <p style={{ margin: 0 }} className="muted">
                Everything keeps working until {formatDate(scheduledEffectiveIso)}, then this workspace
                moves to {scheduledTarget}. Nothing is deleted and no further payment is taken. You can
                call this off at any time before that date.
              </p>
            </div>
            {subscription.canResumeCancellation ? (
              <Button variant="secondary" onClick={plan.onResume} disabled={plan.resumePending}>
                {plan.resumePending ? "Restoring…" : "Keep my plan"}
              </Button>
            ) : null}
          </div>
        ) : null}
        {resumeFeedback ? (
          <p
            className={resumeFeedback.tone === "error" ? "ax-confirm__error" : "muted"}
            role={resumeFeedback.tone === "error" ? "alert" : "status"}
            style={{ marginTop: "0.5rem" }}
          >
            {resumeFeedback.message}
          </p>
        ) : null}
        <div className="row" style={{ gap: "0.75rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
          {/*
            "Payment methods", not "Open customer portal" (Part 15). A customer
            looking for where their card lives was previously offered a button
            named after the provider's product rather than after the thing they
            came to do, so the one required label in this group was the one label
            the page never said. The destination is unchanged, and the line below
            still states plainly that it is a hosted portal and that ODIN never
            handles card details.
          */}
          <Button variant="secondary" onClick={onOpenPortal} disabled={portalPending}>
            {portalPending ? "Opening…" : "Payment methods"}
          </Button>
          {/*
            "Change plan" is what the customer wants; the page it opens compares
            plans and then asks for a review before anything is charged, so the
            label promises no more than it delivers (Part 7.3 — clicking this must
            not upgrade anyone).
          */}
          <Link to="/app/billing/plans">
            <Button variant="ghost">Change plan</Button>
          </Link>
          <Link to="/app/usage">
            <Button variant="ghost">View usage</Button>
          </Link>
          <Link to="/app/invoices">
            <Button variant="ghost">Invoices</Button>
          </Link>
          <Button variant="ghost" onClick={onSync} disabled={syncPending}>
            {syncPending ? "Checking…" : "Check for payment updates"}
          </Button>
        </div>
        {portalError ? (
          <p role="status" className="muted" style={{ marginTop: "0.5rem" }}>
            {portalError}
          </p>
        ) : null}
        {syncMessage ? (
          <p role="status" className="muted" style={{ marginTop: "0.5rem" }}>
            {syncMessage}
          </p>
        ) : null}
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          Paid recently and still see the old plan? “Check for payment updates” re-reads your payment
          provider and applies whatever it finds. It only reads — it never charges anything.
        </p>
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          Payment methods and your billing contact are managed on the secure customer portal — they are
          never handled or stored in this dashboard.
        </p>
      </Card>

      {entitlements ? (
        <Card title="What your plan includes">
          <p className="muted">
            Plan <span className="mono">{entitlements.planCode}</span> · state{" "}
            <span className="mono">{entitlements.subscriptionState}</span>. Safety and billing tools always
            remain available.
          </p>
          {enforcementNotes.length > 0 ? (
            <div style={{ display: "grid", gap: "0.35rem", marginTop: "0.5rem" }}>
              <p className="subhead">Currently limited or paused</p>
              <ul className="chip-list" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                {enforcementNotes.map((e) => (
                  <li key={e.key} className="muted">
                    <strong>{e.key}</strong>: {e.explanation}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="muted" style={{ marginTop: "0.5rem" }}>
              Everything in your plan is currently available.
            </p>
          )}
        </Card>
      ) : null}

      <Card title="Usage this period">
        {usage ? (
          usageWarnings.length > 0 ? (
            <div style={{ display: "grid", gap: "0.35rem" }}>
              <p className="muted">A few metrics are near or at their limit:</p>
              <ul className="chip-list" style={{ flexDirection: "column", alignItems: "flex-start" }}>
                {usageWarnings.map((i) => (
                  <li key={i.metric}>
                    <StatusIndicator
                      tone={quotaTone(i.quota)}
                      label={`${metricMeta(i.metric).label}: ${i.quota.state === "limit-reached" ? "limit reached" : "approaching limit"}`}
                    />
                  </li>
                ))}
              </ul>
              <p className="muted">You are never charged for going over — usage simply shows here.</p>
            </div>
          ) : (
            <p className="muted">Your usage is comfortably within your plan for this period.</p>
          )
        ) : (
          <p className="muted">Usage details are available on the usage page.</p>
        )}
        <div className="row" style={{ marginTop: "0.5rem" }}>
          <Link to="/app/usage">
            <Button variant="ghost">Open usage</Button>
          </Link>
        </div>
      </Card>

      {invoices && invoices.length > 0 ? (
        <Card title="Recent invoices">
          <div className="kv-grid">
            {invoices.slice(0, 3).map((inv) => (
              <div className="kv" key={inv.id}>
                <span className="kv__k">
                  {inv.number ? `#${inv.number}` : "Invoice"} · {formatDate(inv.createdAtIso)}
                </span>
                <span className="kv__v">
                  {inv.totals.total.formatted} · {inv.status}
                </span>
              </div>
            ))}
          </div>
          <div className="row" style={{ marginTop: "0.5rem" }}>
            <Link to="/app/invoices">
              <Button variant="ghost">All invoices</Button>
            </Link>
          </div>
        </Card>
      ) : null}

      {manual && manual.length > 0 ? (
        <Card title="Manual billing agreement">
          {manual.map((agreement) => (
            <div key={agreement.id} className="kv-grid" style={{ marginBottom: "0.5rem" }}>
              <div className="kv">
                <span className="kv__k">Plan</span>
                <span className="kv__v">{agreement.planCode}</span>
              </div>
              <div className="kv">
                <span className="kv__k">Status</span>
                <span className="kv__v">{agreement.status}</span>
              </div>
              <div className="kv">
                <span className="kv__k">Runs until</span>
                <span className="kv__v">{formatDate(agreement.expiryAtIso)}</span>
              </div>
            </div>
          ))}
          <p className="muted">
            This plan is provided under a manual agreement handled directly with the ODIN team, not through
            self-service card billing.
          </p>
        </Card>
      ) : null}

      {cancellation ? (
        <CancellationReviewPanel
          review={cancellation}
          onOpenPortal={onOpenPortal}
          portalPending={portalPending}
          onCancelPlan={plan.onCancel}
          cancelPending={plan.cancelPending}
          cancelError={cancelFeedback?.tone === "error" ? cancelFeedback.message : null}
          cancelNotice={cancelFeedback?.tone === "notice" ? cancelFeedback.message : null}
        />
      ) : null}

      <Card title="Need help with billing?">
        <p className="muted">
          For a billing question, a receipt correction, or anything unclear on an invoice, you can reach the
          ODIN team. We will never ask for card details over support.
        </p>
        <div className="row">
          {/* Part 10.2 — the topic is carried in the link so the customer does not have
              to re-state where they came from. It only pre-selects the topic; nothing
              is opened or sent until they write the message themselves. */}
          <Link to={supportHref("BILLING")}>
            <Button variant="ghost">Billing Support</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; bundle: BillingBundle }
  | { kind: "unavailable"; detail?: string };

async function settledValue<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch {
    return null;
  }
}

export function Billing(): JSX.Element {
  const { tenantId, tenantName } = useAppContext();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [portalPending, setPortalPending] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [syncPending, setSyncPending] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [cancelPending, setCancelPending] = useState(false);
  const [resumePending, setResumePending] = useState(false);
  const [planFeedback, setPlanFeedback] = useState<PlanActionFeedback | null>(null);

  const load = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    setState({ kind: "loading" });
    // The subscription view is the anchor; the rest degrade gracefully if a
    // capability is missing, so the overview still renders honestly.
    api.billing
      .subscription(tenantId)
      .then(async (sub) => {
        const [entitlements, usage, invoices, manual, cancellation] = await Promise.all([
          settledValue(api.billing.entitlements(tenantId)),
          settledValue(api.billing.usage(tenantId)),
          settledValue(api.billing.invoices(tenantId)),
          settledValue(api.billing.manual(tenantId)),
          settledValue(api.billing.cancellationReview(tenantId)),
        ]);
        setState({
          kind: "ready",
          bundle: {
            subscription: sub.subscription,
            entitlements: entitlements?.entitlements ?? null,
            usage: usage ?? null,
            invoices: invoices?.invoices ?? null,
            manual: manual?.agreements ?? null,
            cancellation: cancellation?.review ?? null,
          },
        });
      })
      .catch((error: unknown) =>
        setState({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Ask the server to re-read the payment provider and apply whatever it finds.
   *
   * This is the customer's own recovery path for a delayed or missed provider
   * notification: they paid, the plan did not move, and nobody should have to
   * activate it by hand. READ-ONLY at the provider — it never charges, changes,
   * cancels or refunds anything, and the customer is resolved server-side.
   */
  const onSync = useCallback(async () => {
    if (!tenantId) return;
    setSyncPending(true);
    setSyncMessage(null);
    try {
      const result = await api.billing.sync(tenantId);
      setSyncMessage(syncMessageFor(result));
      // Only re-read the page when something actually changed.
      if (result.synced) load();
    } catch (error) {
      setSyncMessage(
        error instanceof ApiError
          ? error.message
          : "We could not reach the payment provider just now. Please try again shortly.",
      );
    } finally {
      setSyncPending(false);
    }
  }, [tenantId, load]);

  /**
   * Schedule the plan to stop renewing at the end of the period already paid for.
   *
   * Period-end only: nothing is terminated early, nothing is refunded or prorated,
   * no charge is created and nothing is deleted. The dashboard reports ONLY what the
   * server confirms — when the provider cannot schedule it, `scheduled` is false and
   * the customer is told plainly rather than shown a success message.
   */
  const onCancelPlan = useCallback(async () => {
    if (!tenantId) return;
    setCancelPending(true);
    setPlanFeedback(null);
    try {
      const result = await api.billing.cancelPlan(tenantId);
      setPlanFeedback({
        scope: "cancel",
        tone: result.scheduled ? "notice" : "error",
        message: result.message,
      });
      if (result.scheduled) load();
    } catch (error) {
      setPlanFeedback({
        scope: "cancel",
        tone: "error",
        message:
          error instanceof ApiError
            ? error.message
            : "We could not reach billing just now. Nothing has been cancelled — please try again shortly.",
      });
    } finally {
      setCancelPending(false);
    }
  }, [tenantId, load]);

  /** Call off a scheduled cancellation that has not taken effect yet. One click. */
  const onResumePlan = useCallback(async () => {
    if (!tenantId) return;
    setResumePending(true);
    setPlanFeedback(null);
    try {
      const result = await api.billing.resumePlan(tenantId);
      setPlanFeedback({
        scope: "resume",
        tone: result.usePortal ? "error" : "notice",
        message: result.message,
      });
      if (!result.usePortal) load();
    } catch (error) {
      setPlanFeedback({
        scope: "resume",
        tone: "error",
        message:
          error instanceof ApiError
            ? error.message
            : "We could not restore your plan just now. Nothing has changed — please try again shortly.",
      });
    } finally {
      setResumePending(false);
    }
  }, [tenantId, load]);

  const onOpenPortal = useCallback(async () => {
    if (!tenantId) return;
    setPortalPending(true);
    setPortalError(null);
    try {
      const res = await api.billing.openPortal(tenantId);
      window.location.assign(res.portalUrl);
    } catch (error) {
      setPortalError(error instanceof ApiError ? error.message : "The customer portal could not be opened. Please retry.");
      setPortalPending(false);
    }
  }, [tenantId]);

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Billing" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to view billing.
        </NotConfiguredState>
      </>
    );
  }

  if (!tenantId) {
    return (
      <>
        <PageHeader title="Billing" />
        <EmptyState title="Select a workspace">
          Use the workspace menu at the top left to choose a workspace — or create your first one — to
          view billing and choose a plan.
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Billing">
        <p>Plan, usage and invoices for {tenantName ?? "this workspace"}.</p>
      </PageHeader>
      {state.kind === "loading" ? <LoadingState label="Loading billing…" /> : null}
      {state.kind === "unavailable" ? (
        <div className="state" role="alert">
          <ApiUnavailableState detail={state.detail} />
          <Button variant="secondary" onClick={load}>
            Retry
          </Button>
        </div>
      ) : null}
      {state.kind === "ready" ? (
        <BillingOverview
          bundle={state.bundle}
          onOpenPortal={onOpenPortal}
          portalPending={portalPending}
          portalError={portalError}
          onSync={onSync}
          syncPending={syncPending}
          syncMessage={syncMessage}
          plan={{
            onCancel: onCancelPlan,
            onResume: onResumePlan,
            cancelPending,
            resumePending,
            feedback: planFeedback,
          }}
        />
      ) : null}
    </>
  );
}
